import React, { useEffect, useState } from 'react';
import Stack from 'react-bootstrap/Stack';
import {
  CartesianGrid,
  Cell,
  Label,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import {
  CUTOFF_DNR,
  CUTOFF_HR,
  CUTOFF_RWC,
  getNineBoxRecommendationCutoff,
  getRecommendationCutoff,
  GREEN_YELLOW_RED_COLORS,
} from './candidate-scoring-util';
import { useAuth } from '../../providers/auth-provider';
import ClientDashboardCandidateDonuts from './client-dashboard-candidate-donuts';
import { getClientConfiguration } from '../../api/client';
import { getCandidateLoyaltyText, getCandidatePerformanceText } from '../reports/shared/report-util';
import { getPercentileAttributes } from '../../api/dashboards-api';

const getScatterColor = (entry, isLeader) => {
  let cutoff;
  if (isLeader) {
    cutoff = getNineBoxRecommendationCutoff(entry);
  } else {
    cutoff = getRecommendationCutoff(entry);
  }
  switch (cutoff) {
    case CUTOFF_HR:
      return GREEN_YELLOW_RED_COLORS.highlyRecommend.primary;
    case CUTOFF_RWC:
      return GREEN_YELLOW_RED_COLORS.recommendWithCaution.primary;
    case CUTOFF_DNR:
      return GREEN_YELLOW_RED_COLORS.doNotRecommend.primary;
    default:
      return GREEN_YELLOW_RED_COLORS.recommend.primary;
  }
};

const getTooltipColor = (value) => {
  if (value < 33) {
    return GREEN_YELLOW_RED_COLORS.doNotRecommend.primary;
  }

  if (value >= 66) {
    return GREEN_YELLOW_RED_COLORS.highlyRecommend.primary;
  }

  return GREEN_YELLOW_RED_COLORS.recommendWithCaution.primary;
};

function ScatterTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const { performance } = payload[0].payload.percentiles;
    const { loyalty } = payload[0].payload.percentiles;
    const { metadata } = payload[0].payload;
    const performanceColor = getTooltipColor(performance);
    const loyaltyColor = getTooltipColor(loyalty);
    return (
      <div className="custom-tooltip m-0 p-2">
        <div className="label">{payload[0].payload.name}</div>
        <div className="label">{payload[0].payload.position}</div>
        {metadata &&
          metadata.map((value) => {
            return (
              <div key={Math.random()} className="label">
                {value}
              </div>
            );
          })}
        <div className="label">
          <span>Performance:</span>
          <span style={{ color: performanceColor }}>{` ${performance}%`}</span>
        </div>
        <div className="label">
          <span>Loyalty:</span>
          <span style={{ color: loyaltyColor }}>{` ${loyalty}%`}</span>
        </div>
      </div>
    );
  }

  return null;
}

function ClientDashboardScatter({ aggregations, candidates }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [scatterData, setScatterData] = useState([]);

  const mouseEnter = (e) => {
    const event = { ...e };
    event.target.style.cursor = 'pointer';
  };

  const mouseLeave = (e) => {
    const event = { ...e };
    event.target.style.cursor = '';
  };

  useEffect(() => {
    if (candidates?.length) {
      const clientConfiguration = getClientConfiguration();
      const scatterAttributes = [];
      if (auth.isLeader()) {
        const scatterMetadata = clientConfiguration.develop?.scatter?.metadata;
        if (scatterMetadata) {
          scatterAttributes.push(...scatterMetadata);
        }
      }

      const data = [];

      const percentileAttributes = getPercentileAttributes();
      candidates.forEach((candidate) => {
        const displayMetadata = [];
        if (scatterAttributes.length && candidate.metadata) {
          scatterAttributes.forEach((attributeId) => {
            const filtered = Object.values(candidate.metadata).filter((metadata) => {
              return metadata.id === attributeId;
            });
            if (filtered.length > 0) {
              displayMetadata.push(filtered[0].item.value);
            }
          });
        }
        const percentiles = {
          performance: candidate.percentiles[percentileAttributes.performance],
          loyalty: candidate.percentiles[percentileAttributes.loyalty],
        };
        percentiles[percentileAttributes.performance] = candidate.percentiles[percentileAttributes.performance];
        percentiles[percentileAttributes.loyalty] = candidate.percentiles[percentileAttributes.loyalty];

        data.push({
          name: candidate.name,
          position: candidate.position,
          reference: candidate.reference,
          percentiles,
          metadata: displayMetadata.length ? displayMetadata : null,
        });
      });
      setScatterData(data);
    } else {
      setScatterData([]);
    }
  }, [aggregations]);

  const { isDevelop, isSelect } = useAuth();

  const goToCandidateProfile = (clientReference) => {
    navigate(`/reports/${clientReference}`);
  };

  return (
    <>
      {isDevelop() ? (
        <div className="chart-title pb-2"> Talent Prediction</div>
      ) : (
        <div className="chart-title pb-2"> Candidate Quality</div>
      )}
      <Stack className="h-75">
        {isSelect() && <ClientDashboardCandidateDonuts aggregations={aggregations} />}
        <div className="row pt-5 pb-5">
          <ResponsiveContainer width="85%" aspect={2}>
            <ScatterChart>
              <CartesianGrid />
              <XAxis type="number" dataKey="percentiles.performance" tick={false} domain={[0, 100]}>
                <Label value={getCandidatePerformanceText(isSelect(), isDevelop())} />
              </XAxis>
              <YAxis type="number" dataKey="percentiles.loyalty" tick={false} domain={[0, 100]}>
                <Label value={getCandidateLoyaltyText(isSelect(), isDevelop())} angle={-90} />
              </YAxis>
              <Scatter data={scatterData} fill="#8884d8">
                <>
                  {scatterData.map((entry) => (
                    <Cell
                      key={`cell-${Math.random()}`}
                      fill={getScatterColor(entry, isDevelop())}
                      onClick={() => goToCandidateProfile(entry.reference)}
                      onMouseOver={(event) => mouseEnter(event)}
                      onMouseOut={(event) => mouseLeave(event)}
                    />
                  ))}
                </>
              </Scatter>
              <Tooltip content={<ScatterTooltip />} />
              <ReferenceLine x={33} stroke="#AFAFAF" />
              <ReferenceLine y={33} stroke="#AFAFAF" />
              <ReferenceLine x={66} stroke="#AFAFAF" />
              <ReferenceLine y={66} stroke="#AFAFAF" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </Stack>
    </>
  );
}

export default ClientDashboardScatter;
